import {DatePicker, Form, Input, InputNumber, Spin} from 'antd';
import moment from 'moment';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import v2 from '../v2/instrumentV2.module.scss';
import {FormCard, spans, Hint, derivedClass, DerivedTag} from '../v2/FormCard';
import {InstrumentActions} from '../v2/InstrumentActions';
import {EQUITY_DETAILS_MOCK} from '../v2/devPrefill';
import {useRemainingFields} from '../v2/useRemainingFields';
import {groupThousands, parseThousands} from '../v2/numberFormat';
import {handleEquityFieldState} from 'redux/store/assetClass';
import {GET_PUBLIC_EQUITY_BY_ID} from 'graphql/queries/assetManagement';
import {UPDATE_PUBLIC_EQUITY} from 'graphql/mutations/assetManagement';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import DBModal from 'components/modal/modal';
import {CreatePublicEquityFinishModal} from './CreatePublicEquityFinishModal';

/**
 * Public equity specifics.
 *
 * The input is deliberately small — listing date, dividend and the latest
 * quote. `sharesOutstanding`, `keyMetrics`, `ranking` and `sourceIpoId` are
 * readable but absent from both PublicEquityInput and the base input, so they
 * are shown as derived rather than collected.
 */
export const PublicEquityDetails = () => {
  const [form] = Form.useForm();
  const {id, lang} = useParams();
  const dispatch = useDispatch();
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [updatedValues, setUpdatedValues] = useState({});

  const draft = useSelector(state => state?.assetClass?.equity) || {};
  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const [updatePublicEquity, {loading: loadingUpdate}] =
    useMutation(UPDATE_PUBLIC_EQUITY);
  const {data, refetch, loading} = useQuery(GET_PUBLIC_EQUITY_BY_ID, {
    // The language versions table links here with the variant's language in
    // the URL; without passing it the read always came back in the base
    // language and the form silently showed the wrong translation.
    variables: {getFinancialInstrumentId: id, language: lang},
    skip: !id,
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const [remaining, onValuesChange] = useRemainingFields(
    form,
    ['ticker', 'listedAt'],
    data,
  );

  const applyValues = source => {
    if (!source) return;
    form.setFieldsValue({
      ticker: source.ticker,
      isin: source.isin,
      listedAt: source.listedAt ? moment(source.listedAt) : null,
      dividendYield: source.dividend?.yield,
      amountPerShare: source.dividend?.amountPerShare,
      exDate: source.dividend?.exDate ? moment(source.dividend.exDate) : null,
      nextDate: source.dividend?.nextDate
        ? moment(source.dividend.nextDate)
        : null,
      lastDate: source.dividend?.lastDate
        ? moment(source.dividend.lastDate)
        : null,
      lastPrice: source.latestQuote?.lastPrice,
      openingPrice: source.latestQuote?.openingPrice,
      closingPrice: source.latestQuote?.closingPrice,
      highPrice: source.latestQuote?.highPrice,
      lowPrice: source.latestQuote?.lowPrice,
      volume: source.latestQuote?.volume,
    });
  };

  useEffect(() => {
    applyValues(data?.getFinancialInstrument?.data);
  }, [data?.getFinancialInstrument?.data]);

  useEffect(() => {
    if (!id && draft?.ticker) applyValues(draft);
  }, [draft]);

  const buildPayload = values => ({
    // ticker and isin are collected here but belong to the base input; the
    // create modal partitions them out.
    ticker: values.ticker,
    isin: values.isin,
    listedAt: values.listedAt,
    dividend: {
      yield: values.dividendYield,
      amountPerShare: values.amountPerShare,
      exDate: values.exDate,
      nextDate: values.nextDate,
      lastDate: values.lastDate,
    },
    latestQuote: {
      lastPrice: values.lastPrice,
      openingPrice: values.openingPrice,
      closingPrice: values.closingPrice,
      highPrice: values.highPrice,
      lowPrice: values.lowPrice,
      volume: values.volume,
    },
  });

  if (loading) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  const record = data?.getFinancialInstrument?.data;

  return (
    <div>
      <div className={v2.bodyHead}>
        <h2>Equity details</h2>
        <p>
          Listing date, dividend policy and the latest quote. Market
          capitalisation and rank are computed by the platform.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={onValuesChange}
        onFinish={values => {
          if (id) {
            setOpenUpdateModal(true);
            setUpdatedValues(buildPayload(values));
          } else {
            dispatch(handleEquityFieldState(buildPayload(values)));
            setOpenCreateModal(true);
          }
        }}>
        <FormCard
          title="Listing"
          description="How this equity is quoted, and when it began trading">
          <div className={spans.half}>
            <Form.Item
              name="ticker"
              label="Ticker"
              rules={[{required: true, message: 'Ticker is required'}]}>
              <Input placeholder="SNTS" />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="isin" label="ISIN">
              <Input />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="listedAt" label="Listed at">
              <DatePicker
                className={v2.fullWidth}
                showTime={{defaultValue: moment('00:00', 'HH:mm:ss')}}
                format="DD MMM YYYY, HH:mm"
              />
            </Form.Item>
          </div>
          <div className={`${spans.half} ${derivedClass}`}>
            <Form.Item
              label={
                <>
                  Shares outstanding <DerivedTag />
                </>
              }>
              <InputNumber
                disabled
                value={record?.sharesOutstanding ?? undefined}
                formatter={groupThousands}
              />
            </Form.Item>
            <Hint>
              Readable but absent from the equity input — it cannot be set from
              admin today.
            </Hint>
          </div>
          {record?.ranking && (
            <div className={`${spans.full} ${derivedClass}`}>
              <Form.Item
                label={
                  <>
                    Rank <DerivedTag />
                  </>
                }>
                <InputNumber
                  disabled
                  value={record.ranking.position}
                  formatter={v =>
                    `${v} of ${record.ranking.outOf} by ${(
                      record.ranking.basis || ''
                    ).toLowerCase()}`
                  }
                />
              </Form.Item>
            </div>
          )}
        </FormCard>

        <FormCard
          title="Dividend"
          description="What holders are paid, and when">
          <div className={spans.half}>
            <Form.Item name="dividendYield" label="Yield (%)">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="amountPerShare" label="Amount per share">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item name="exDate" label="Ex-dividend date">
              <DatePicker className={v2.fullWidth} format="DD MMM YYYY" />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item name="nextDate" label="Next payment">
              <DatePicker className={v2.fullWidth} format="DD MMM YYYY" />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item name="lastDate" label="Last payment">
              <DatePicker className={v2.fullWidth} format="DD MMM YYYY" />
            </Form.Item>
          </div>
        </FormCard>

        <FormCard
          title="Latest quote"
          description="Overwritten by the market-data feed where one is connected">
          <div className={spans.third}>
            <Form.Item name="lastPrice" label="Last price">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item name="openingPrice" label="Opening price">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item name="closingPrice" label="Closing price">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item name="highPrice" label="High">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item name="lowPrice" label="Low">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item name="volume" label="Volume">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={`${spans.half} ${derivedClass}`}>
            <Form.Item
              label={
                <>
                  Market capitalisation <DerivedTag />
                </>
              }>
              <InputNumber
                disabled
                value={record?.latestQuote?.marketCapitalization ?? undefined}
                formatter={groupThousands}
              />
            </Form.Item>
            <Hint>Derived from shares outstanding × price.</Hint>
          </div>
        </FormCard>

        <InstrumentActions
          onPrepopulate={() => {
            form.setFieldsValue(EQUITY_DETAILS_MOCK);
            onValuesChange();
          }}
          remaining={remaining}
          disabled={!pageOperations?.update}
          label={id ? 'Update' : 'Create equity'}
        />
      </Form>

      <DBModal
        isOpen={openCreateModal}
        handleClose={() => setOpenCreateModal(false)}
        content={
          <CreatePublicEquityFinishModal
            handleClose={() => setOpenCreateModal(false)}
          />
        }
      />

      <ConfirmModal
        openModal={openUpdateModal}
        setOpenModal={() => setOpenUpdateModal(false)}
        loading={loadingUpdate}
        title="Update equity"
        confirmText="Are you sure you want to update this equity?"
        handleOperation={() =>
          updatePublicEquity({
            variables: {updatePublicEquityId: id, input: updatedValues},
          })
            .then(({data: {updatePublicEquity: res}}) => {
              if (res?.__typename === 'Error') {
                toast.error(res?.message);
              } else {
                toast.success('Equity updated');
                setOpenUpdateModal(false);
                refetch();
              }
            })
            .catch(error => toast.error(error.message))
        }
      />
    </div>
  );
};

export default PublicEquityDetails;
